import { Copy, Download } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { SEO } from "@/components/Seo";

const ASSETS = [
  {
    file: "/press-kit/ig-post-launch.png",
    name: "Launch Post",
    size: "1080 × 1080 · Feed post",
    caption:
      "Real food. Real science. Real prana.\n\nWe're Pranivaa Kitchens — a clean vegetarian & vegan food brand that explains the science behind everyday Indian ingredients. No fads. No fear. Just your kitchen, decoded.\n\nLaunching first in Delhi NCR & Bengaluru. Join the waitlist — link in bio.\n\n#PranivaaKitchens #CleanEating #PlantForward #VeganIndia #VegetarianIndia #DelhiNCR #Bengaluru #HealthyIndianFood #FoodScience",
  },
  {
    file: "/press-kit/ig-post-navratri.png",
    name: "Navratri Drop Post",
    size: "1080 × 1080 · Feed post",
    caption:
      "The Navratri Drop is coming. Nine vrat-friendly bowls — samak, kuttu, sabudana, sendha namak — fasting food that actually tastes like a celebration.\n\n11 October 2026. Delhi NCR & Bengaluru. Waitlist members order 48 hours before everyone else. Link in bio.\n\n#Navratri2026 #NavratriFood #VratFriendly #PranivaaKitchens #FestiveFood #CleanEating #DelhiNCR #Bengaluru",
  },
  {
    file: "/press-kit/ig-post-science.png",
    name: "Science Hub Post",
    size: "1080 × 1080 · Feed post",
    caption:
      "Six conditions. Zero jargon.\n\nDiabetes. PCOS. Thyroid. Gut health. Heart health. Weight. Our Science Hub explains what clean plant-forward eating actually does in your body — in plain language, with Indian food at the centre. Educational, never preachy.\n\nRead the guides — link in bio.\n\n#FoodScience #PCOSIndia #DiabetesIndia #GutHealth #ThyroidHealth #HeartHealth #PranivaaKitchens #NutritionIndia",
  },
  {
    file: "/press-kit/ig-story-drop.png",
    name: "Drop Story",
    size: "1080 × 1920 · Story (add your link sticker over the gold pill)",
    caption:
      "Story tip: place your Instagram link sticker directly over the gold JOIN THE WAITLIST pill, pointing to your site. Add a countdown sticker set to 11 Oct for extra lift.",
  },
];

export default function PressKit() {
  const copy = async (text, name) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${name} caption copied.`);
    } catch {
      toast.error("Copy failed — select the text manually.");
    }
  };

  return (
    <div data-testid="press-kit-page">
      <SEO
        title="Press Kit | Pranivaa Kitchens"
        description="Branded Instagram graphics and captions for Pranivaa Kitchens."
        path="/press-kit"
        noindex
      />
      <section className="pt-28 md:pt-40 pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-clay font-semibold mb-5">
              Instagram Bio Kit
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.06] text-ink font-semibold max-w-3xl mb-6">
              Branded posts, ready to publish.
            </h1>
            <p className="text-base md:text-lg leading-relaxed text-ink-soft max-w-2xl">
              Download the graphics, tap to copy the matching caption, and post. Everything follows the
              emerald, gold, cream and peach brand system.
            </p>
          </Reveal>
        </div>
      </section>
      <section className="pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {ASSETS.map((a, i) => (
              <StaggerItem key={a.file} testId={`press-kit-card-${i + 1}`}>
                <div className="bg-paper-card border border-[#E7E2D7] rounded-3xl overflow-hidden h-full flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                  <div className="bg-beet-deep p-6 flex justify-center">
                    <img loading="lazy" decoding="async"
                      src={a.file}
                      alt={`${a.name} graphic`}
                      className={`rounded-2xl shadow-lg object-cover ${a.size.includes("Story") ? "h-80" : "h-80 w-80"}`}
                    />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div>
                        <h2 className="font-serif text-xl font-semibold text-ink">{a.name}</h2>
                        <p className="font-mono text-[11px] text-ink-muted mt-1">{a.size}</p>
                      </div>
                      <a
                        href={a.file}
                        download
                        data-testid={`press-kit-download-${i + 1}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-beet text-white text-xs font-bold px-4 py-2.5 transition-all hover:bg-beet-deep shrink-0"
                      >
                        <Download size={13} /> PNG
                      </a>
                    </div>
                    <div className="bg-paper-muted/60 border border-[#E7E2D7] rounded-xl p-4 flex-1">
                      <p className="text-xs text-ink-soft leading-relaxed whitespace-pre-line">{a.caption}</p>
                    </div>
                    <button
                      data-testid={`press-kit-copy-${i + 1}`}
                      onClick={() => copy(a.caption, a.name)}
                      className="mt-4 inline-flex items-center justify-center gap-2 rounded-full border-2 border-ink/15 text-ink text-xs font-bold px-4 py-3 transition-all hover:border-ink"
                    >
                      <Copy size={13} /> Copy Caption
                    </button>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal className="mt-10">
            <p className="text-xs font-mono text-ink-muted leading-relaxed max-w-2xl">
              Bio tip: set your Instagram bio link to the Coming Soon page so every profile visit lands
              on the waitlist with the live drop countdown.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
